export type LotteryRoundRealtimePayload = {
  _id?: string
  lotteryRoundId?: string
  roundId?: string
  roundNumber?: string
  status?: string
  isOpen?: boolean
  newStatus?: string
  newIsOpen?: boolean
  betEndTime?: string
  isActive?: boolean
  results?: unknown
  result?: string
  [key: string]: unknown
}

export function normalizeRoundId(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object' && value !== null && '_id' in (value as object)) {
    return normalizeRoundId((value as { _id?: unknown })._id)
  }
  return String(value).trim()
}

export function extractRoundFromRealtimeDetail(detail: unknown): LotteryRoundRealtimePayload | null {
  if (!detail || typeof detail !== 'object') return null
  const record = detail as Record<string, unknown>
  const round = record.round ?? record.data
  if (round && typeof round === 'object') return round as LotteryRoundRealtimePayload
  return null
}

export function resolveRoundIdFromPayload(round: LotteryRoundRealtimePayload | null | undefined): string {
  if (!round) return ''
  return normalizeRoundId(round._id ?? round.lotteryRoundId ?? round.roundId)
}

export function isLotteryRoundClosed(
  round: { status?: string; isOpen?: boolean; newStatus?: string; newIsOpen?: boolean } | null | undefined,
): boolean {
  if (!round) return false
  const isOpen = round.isOpen ?? round.newIsOpen
  if (isOpen === false) return true
  const status = String(round.status ?? round.newStatus ?? '').toLowerCase()
  return status === 'closed' || status === 'cancelled' || status === 'announced' || status === 'completed'
}

function roundHasAnyResult(
  round: { results?: unknown; result?: string } | null | undefined,
): boolean {
  if (!round) return false
  const results = round.results as { winningNumbers?: Record<string, unknown> } | undefined
  const wn = results?.winningNumbers
  if (wn && typeof wn === 'object') {
    return Object.values(wn).some((value) => value != null && String(value).trim() !== '')
  }
  return !!(round.result && String(round.result).trim())
}

/**
 * หน้าแสดงผลหวย — ซ่อนรอบที่แอดมินปิดก่อนเวลาปิดรับตามปกติ
 * แต่ยังแสดงรอบที่ปิดรับตามเวลาแล้วรอประกาศผล (รอผล) / ยกเลิก / มีผลแล้ว
 */
export function isRoundEligibleForResultsDisplay(
  round:
    | {
        status?: string
        isOpen?: boolean
        betEndTime?: string
        isActive?: boolean
        results?: unknown
        result?: string
      }
    | null
    | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!round) return false
  if (round.isActive === false) return false

  const status = String(round.status ?? '').toLowerCase()
  if (status === 'cancelled') return true
  if (roundHasAnyResult(round)) return true
  if (status === 'announced' || status === 'completed') return true

  const betEnd = round.betEndTime ? new Date(round.betEndTime).getTime() : Number.NaN
  const pastBetEnd = Number.isFinite(betEnd) && nowMs >= betEnd
  if (pastBetEnd) return true

  const isOpen = round.isOpen
  if (isOpen === true && status === 'open') return true

  if (isOpen === false || status === 'closed' || status === 'suspended') return false

  return true
}

export function mergeRoundIntoList<T extends { _id?: string }>(list: T[], incoming: T): T[] {
  const incomingId = normalizeRoundId(incoming._id)
  if (!incomingId) return list

  const isActive = (incoming as { isActive?: boolean }).isActive
  const index = list.findIndex((round) => normalizeRoundId(round._id) === incomingId)
  if (isActive === false) {
    if (index === -1) return list
    return list.filter((_, i) => i !== index)
  }
  if (index === -1) return list
  return list.map((round, i) => (i === index ? { ...round, ...incoming } : round))
}
